import { api } from '@/lib/api';
import type {
  CampaignResponse,
  CampaignListResponse,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  CampaignQuery,
  CampaignStatsResponse,
  CampaignMessageListResponse,
  LeadListResponse,
  ContactListResponse,
} from '@troia-v3/types';

/**
 * Campaigns Service
 * Handles all API calls related to campaigns
 */
export const campaignsService = {
  /**
   * Get paginated list of campaigns
   */
  async getCampaigns(query?: CampaignQuery): Promise<CampaignListResponse> {
    const response = await api.get('/campaigns', { params: query });
    return response.data.data;
  },

  /**
   * Get campaign by ID
   */
  async getCampaignById(id: string): Promise<CampaignResponse> {
    const response = await api.get(`/campaigns/${id}`);
    return response.data.data;
  },

  /**
   * Create new campaign
   */
  async createCampaign(data: CreateCampaignRequest): Promise<CampaignResponse> {
    console.log('🚀 [campaignsService] createCampaign data:', JSON.stringify(data, null, 2));
    const response = await api.post('/campaigns', data);
    return response.data.data;
  },

  /**
   * Update campaign
   */
  async updateCampaign(id: string, data: UpdateCampaignRequest): Promise<CampaignResponse> {
    const response = await api.put(`/campaigns/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete campaign
   */
  async deleteCampaign(id: string): Promise<void> {
    await api.delete(`/campaigns/${id}`);
  },

  /**
   * Update campaign status
   */
  async updateCampaignStatus(id: string, status: string): Promise<CampaignResponse> {
    const response = await api.patch(`/campaigns/${id}/status`, { status });
    return response.data.data;
  },

  /**
   * Get campaigns by status
   */
  async getCampaignsByStatus(status: string): Promise<CampaignResponse[]> {
    const response = await api.get(`/campaigns/status/${status}`);
    return response.data.data;
  },

  /**
   * Activate campaign (draft → scheduled)
   * Schedules the campaign for execution
   */
  async activateCampaign(id: string): Promise<CampaignResponse> {
    const response = await api.patch(`/campaigns/${id}/activate`);
    return response.data.data;
  },

  /**
   * Pause campaign (in_progress → paused)
   * Pauses the campaign execution, pending messages will not be processed
   */
  async pauseCampaign(id: string): Promise<CampaignResponse> {
    const response = await api.post(`/campaigns/${id}/pause`);
    return response.data.data;
  },

  /**
   * Resume campaign (paused → in_progress)
   * Resumes the campaign execution
   */
  async resumeCampaign(id: string): Promise<CampaignResponse> {
    const response = await api.post(`/campaigns/${id}/resume`);
    return response.data.data;
  },

  /**
   * Cancel campaign (scheduled/in_progress/paused → cancelled)
   * Removes pending jobs from queue and marks pending messages as cancelled
   */
  async cancelCampaign(id: string): Promise<CampaignResponse> {
    const response = await api.post(`/campaigns/${id}/cancel`);
    return response.data.data;
  },

  /**
   * Duplicate campaign (clona config; a cópia nasce como DRAFT com stats zerados)
   */
  async duplicateCampaign(id: string): Promise<CampaignResponse> {
    const response = await api.post(`/campaigns/${id}/duplicate`);
    return response.data.data;
  },

  /**
   * Get campaign statistics (delivery rate, read rate, etc.)
   */
  async getCampaignStats(id: string): Promise<CampaignStatsResponse> {
    const response = await api.get(`/campaigns/${id}/stats`);
    return response.data.data;
  },

  /**
   * Get campaign messages with pagination and filters
   */
  async getCampaignMessages(id: string, query?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<CampaignMessageListResponse> {
    const response = await api.get(`/campaigns/${id}/messages`, { params: query });
    return response.data.data;
  },

  /**
   * Send test message for a campaign
   */
  async sendTestMessage(id: string, data: {
    testRecipients: string[];
    variableMapping: Record<number, { source: string; fieldName?: string; staticValue?: string }>;
  }): Promise<{ results: Array<{ recipientIdentifier: string; success: boolean; error?: string }> }> {
    const response = await api.post(`/campaigns/${id}/test`, data);
    return response.data.data;
  },

  /**
   * Get audience leads with server-side search, pagination and filters
   */
  async getAudienceLeads(query?: {
    page?: number;
    limit?: number;
    search?: string;
    channelId?: string;
    filters?: Record<string, unknown>;
  }): Promise<LeadListResponse> {
    const params: Record<string, unknown> = {
      page: query?.page,
      limit: query?.limit,
      search: query?.search,
      channelId: query?.channelId,
      // Aninhado de propósito: o Zod da rota espera `filters` como objeto
      // (`filters[tags]=x` no query string). Achatado no root, os filtros
      // avançados eram descartados em silêncio.
      filters: query?.filters,
    };
    const response = await api.get('/campaigns/audience/leads', { params });
    return response.data.data;
  },

  /**
   * Get audience contacts with server-side search, pagination and filters
   */
  async getAudienceContacts(query?: {
    page?: number;
    limit?: number;
    search?: string;
    channelId?: string;
    filters?: Record<string, unknown>;
  }): Promise<ContactListResponse> {
    const params: Record<string, unknown> = {
      page: query?.page,
      limit: query?.limit,
      search: query?.search,
      channelId: query?.channelId,
      // Aninhado de propósito: o Zod da rota espera `filters` como objeto
      // (`filters[tags]=x` no query string). Achatado no root, os filtros
      // avançados eram descartados em silêncio.
      filters: query?.filters,
    };
    const response = await api.get('/campaigns/audience/contacts', { params });
    return response.data.data;
  },

  /**
   * Get distinct tags for audience filter dropdown
   */
  async getAudienceTags(): Promise<string[]> {
    const response = await api.get('/campaigns/audience/tags');
    return response.data.data;
  },
};
